import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import {
  CircleAlertIcon,
  CircleCheckIcon,
  InfoIcon,
  TriangleAlertIcon,
  type LucideIcon,
} from "lucide-react"

import { cn } from "../../lib/utils"

const helperMessageVariants = cva(
  "flex w-full items-start gap-2 rounded-md border px-3 py-2 text-sm leading-relaxed",
  {
    variants: {
      tone: {
        neutral: "border-border/70 bg-muted/50 text-muted-foreground",
        // Dark-mode text sits at the 300 step, not 200. A helper message is an ASIDE — at 200 the tinted
        // text was brighter than the body copy around it, so an advisory note out-shouted the content it was
        // annotating. 300 keeps the hue legible against the tinted panel without competing for attention.
        info: "border-sky-500/30 bg-sky-500/10 text-sky-700 dark:text-sky-300",
        success: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
        warning: "border-amber-500/35 bg-amber-500/12 text-amber-700 dark:text-amber-300",
        danger: "border-destructive/30 bg-destructive/10 text-destructive",
      },
      size: {
        sm: "text-xs",
        md: "text-sm",
      },
    },
    defaultVariants: {
      tone: "info",
      size: "sm",
    },
  },
)

const iconByTone: Record<NonNullable<VariantProps<typeof helperMessageVariants>["tone"]>, LucideIcon> = {
  neutral: InfoIcon,
  info: InfoIcon,
  success: CircleCheckIcon,
  warning: TriangleAlertIcon,
  danger: CircleAlertIcon,
}

export type HelperMessageProps = React.ComponentProps<"div"> &
  VariantProps<typeof helperMessageVariants> & {
    icon?: LucideIcon
    hideIcon?: boolean
    title?: string
  }

export function HelperMessage({
  className,
  tone,
  size,
  icon,
  hideIcon = false,
  title,
  children,
  ...props
}: HelperMessageProps) {
  const resolvedTone = tone ?? "info"
  const Icon = icon ?? iconByTone[resolvedTone]

  return (
    <div className={cn(helperMessageVariants({ tone: resolvedTone, size }), className)} {...props}>
      {!hideIcon ? <Icon className='mt-0.5 size-4 shrink-0' aria-hidden='true' /> : null}
      <div className='min-w-0 space-y-0.5'>
        {title ? <p className='font-medium'>{title}</p> : null}
        <div>{children}</div>
      </div>
    </div>
  )
}
